(() => {
  if (window.productTrueZoomInitialized) return;
  window.productTrueZoomInitialized = true;

  const GALLERY_SELECTOR = 'media-gallery';
  const IMAGE_OPENER_SELECTOR = '.product__modal-opener--image';
  const ZOOM_MEDIA_SELECTOR = 'script[data-product-true-zoom-media]';
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const WHEEL_ZOOM_STEP = 1.18;
  const BUTTON_ZOOM_STEP = 1.35;
  const DOUBLE_CLICK_SCALE = 2.5;
  const HIGH_RES_WIDTH = 4096;

  const zoomMaps = new WeakMap();
  const pointers = new Map();

  let elements = null;
  let active = false;
  let imageSequence = [];
  let currentIndex = -1;
  let opener = null;
  let lastFocusedElement = null;

  const state = {
    scale: MIN_SCALE,
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
    startTranslateX: 0,
    startTranslateY: 0,
    dragStarted: false,
    ignoreStageClick: false,
    pinch: null,
    fallbackSrc: '',
    triedFallback: false,
  };

  function init() {
    document.addEventListener('click', handleProductMediaClick, true);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
  }

  function handleProductMediaClick(event) {
    if (event.defaultPrevented) return;

    const mediaOpener = event.target.closest(`${GALLERY_SELECTOR} ${IMAGE_OPENER_SELECTOR}`);
    if (!mediaOpener || !isImageOpener(mediaOpener)) return;

    const gallery = mediaOpener.closest(GALLERY_SELECTOR);
    const image = mediaOpener.querySelector('img');
    const mediaId = getMediaId(mediaOpener, event.target);
    if (!gallery || !image || !mediaId) return;

    const imageData = getZoomImageData(gallery, mediaId, image);
    if (!imageData.src) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    openZoom(imageData, mediaOpener, gallery, mediaId);
  }

  function isImageOpener(mediaOpener) {
    return (
      mediaOpener.classList.contains('product__modal-opener--image') &&
      !mediaOpener.querySelector('deferred-media, video, model-viewer, iframe')
    );
  }

  function getMediaId(mediaOpener, eventTarget) {
    const targetMedia = eventTarget.closest('[data-media-id]');
    const innerMedia = mediaOpener.querySelector('[data-media-id]');
    const parentMedia = mediaOpener.closest('[data-media-id]');

    return (
      targetMedia?.getAttribute('data-media-id') ||
      innerMedia?.getAttribute('data-media-id') ||
      parentMedia?.getAttribute('data-media-id') ||
      ''
    );
  }

  function getZoomImageData(gallery, mediaId, image) {
    const mediaMap = getZoomMap(gallery);
    const mappedImage = mediaMap[mediaId];
    const fallbackSrc = getHighResUrl(getBestImageUrl(image));

    if (mappedImage?.src) {
      return {
        src: mappedImage.src,
        fallbackSrc,
        alt: mappedImage.alt || image.alt || '',
      };
    }

    return {
      src: fallbackSrc || image.currentSrc || image.src,
      fallbackSrc: image.currentSrc || image.src,
      alt: image.alt || '',
    };
  }

  function getZoomMap(gallery) {
    if (zoomMaps.has(gallery)) return zoomMaps.get(gallery);

    const script = gallery.querySelector(ZOOM_MEDIA_SELECTOR);
    if (!script) {
      zoomMaps.set(gallery, {});
      return {};
    }

    try {
      const map = JSON.parse(script.textContent || '{}');
      zoomMaps.set(gallery, map);
      return map;
    } catch (error) {
      zoomMaps.set(gallery, {});
      return {};
    }
  }

  function getBestImageUrl(image) {
    const candidates = (image.getAttribute('srcset') || '')
      .split(',')
      .map((candidate) => candidate.trim().match(/^(.+?)\s+(\d+)w$/))
      .filter(Boolean)
      .map((candidate) => ({
        url: candidate[1],
        width: Number(candidate[2]),
      }))
      .sort((first, second) => second.width - first.width);

    return candidates[0]?.url || image.currentSrc || image.src || '';
  }

  function getHighResUrl(url) {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url, window.location.href);
      const isShopifyImage =
        parsedUrl.hostname.includes('cdn.shopify.com') || parsedUrl.hostname.includes('shopifycdn.net');

      if (isShopifyImage) {
        parsedUrl.searchParams.set('width', HIGH_RES_WIDTH.toString());
      }

      return parsedUrl.toString();
    } catch (error) {
      return url;
    }
  }

  function buildImageSequence(gallery) {
    const mediaItems = Array.from(gallery.querySelectorAll('[data-media-id]'));
    const seenMediaIds = new Set();

    return mediaItems.reduce((sequence, mediaItem) => {
      const mediaId = mediaItem.getAttribute('data-media-id');
      const mediaOpener = mediaItem.matches(IMAGE_OPENER_SELECTOR)
        ? mediaItem
        : mediaItem.querySelector(IMAGE_OPENER_SELECTOR);
      const image = mediaOpener?.querySelector('img');

      if (!mediaId || !mediaOpener || !image || seenMediaIds.has(mediaId) || !isImageOpener(mediaOpener)) {
        return sequence;
      }

      seenMediaIds.add(mediaId);
      sequence.push({
        mediaId,
        opener: mediaOpener,
        ...getZoomImageData(gallery, mediaId, image),
      });

      return sequence;
    }, []);
  }

  function ensureLightbox() {
    if (elements) return;

    const root = document.createElement('div');
    root.className = 'product-true-zoom';
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = `
      <div class="product-true-zoom__backdrop" data-product-true-zoom-close></div>
      <div
        class="product-true-zoom__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Agrandissement de l'image produit"
        tabindex="-1"
      >
        <button type="button" class="product-true-zoom__close" data-product-true-zoom-close aria-label="Fermer">
          <span aria-hidden="true">&times;</span>
        </button>
        <button type="button" class="product-true-zoom__button product-true-zoom__nav product-true-zoom__nav--previous" data-product-true-zoom-previous aria-label="Image precedente">
          <span aria-hidden="true">&#8249;</span>
        </button>
        <button type="button" class="product-true-zoom__button product-true-zoom__nav product-true-zoom__nav--next" data-product-true-zoom-next aria-label="Image suivante">
          <span aria-hidden="true">&#8250;</span>
        </button>
        <div class="product-true-zoom__stage">
          <div class="product-true-zoom__loader" aria-hidden="true"></div>
          <img class="product-true-zoom__image" alt="" draggable="false">
        </div>
        <div class="product-true-zoom__toolbar" aria-label="Commandes de zoom">
          <button type="button" class="product-true-zoom__button" data-product-true-zoom-out aria-label="Zoom arriere">-</button>
          <button type="button" class="product-true-zoom__button" data-product-true-zoom-in aria-label="Zoom avant">+</button>
        </div>
      </div>
    `;

    document.body.appendChild(root);

    elements = {
      root,
      dialog: root.querySelector('.product-true-zoom__dialog'),
      backdrop: root.querySelector('.product-true-zoom__backdrop'),
      stage: root.querySelector('.product-true-zoom__stage'),
      image: root.querySelector('.product-true-zoom__image'),
      closeButton: root.querySelector('.product-true-zoom__close'),
      previousButton: root.querySelector('[data-product-true-zoom-previous]'),
      nextButton: root.querySelector('[data-product-true-zoom-next]'),
      zoomInButton: root.querySelector('[data-product-true-zoom-in]'),
      zoomOutButton: root.querySelector('[data-product-true-zoom-out]'),
    };

    root.addEventListener('click', handleLightboxClick);
    elements.stage.addEventListener('wheel', handleWheel, { passive: false });
    elements.stage.addEventListener('dblclick', handleDoubleClick);
    elements.stage.addEventListener('pointerdown', handlePointerDown);
    elements.stage.addEventListener('pointermove', handlePointerMove);
    elements.stage.addEventListener('pointerup', handlePointerUp);
    elements.stage.addEventListener('pointercancel', handlePointerUp);
    elements.previousButton.addEventListener('click', () => showSiblingImage(-1));
    elements.nextButton.addEventListener('click', () => showSiblingImage(1));
    elements.zoomInButton.addEventListener('click', () => zoomFromCenter(state.scale * BUTTON_ZOOM_STEP));
    elements.zoomOutButton.addEventListener('click', () => zoomFromCenter(state.scale / BUTTON_ZOOM_STEP));
  }

  function openZoom(imageData, sourceOpener, gallery, mediaId) {
    ensureLightbox();

    imageSequence = buildImageSequence(gallery);
    currentIndex = imageSequence.findIndex((item) => item.mediaId === mediaId);

    if (currentIndex < 0) {
      currentIndex = 0;
      imageSequence.unshift({
        mediaId,
        opener: sourceOpener,
        ...imageData,
      });
    }

    opener = sourceOpener;
    lastFocusedElement = document.activeElement;

    resetTransform();
    active = true;

    elements.image.onload = handleImageLoad;
    elements.image.onerror = handleImageError;
    elements.root.hidden = false;
    elements.root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('product-true-zoom-open');
    document.body.classList.add('product-true-zoom-open');
    updateNavigationControls();
    showImageAt(currentIndex);

    requestAnimationFrame(() => {
      elements.closeButton.focus({ preventScroll: true });
    });
  }

  function closeZoom() {
    if (!active || !elements) return;

    active = false;
    imageSequence = [];
    currentIndex = -1;
    pointers.clear();
    elements.root.hidden = true;
    elements.root.setAttribute('aria-hidden', 'true');
    elements.root.classList.remove('is-loading', 'is-zoomed', 'is-dragging', 'has-error', 'has-navigation');
    elements.image.onload = null;
    elements.image.onerror = null;
    elements.image.removeAttribute('src');
    document.documentElement.classList.remove('product-true-zoom-open');
    document.body.classList.remove('product-true-zoom-open');

    if (lastFocusedElement && document.contains(lastFocusedElement)) {
      lastFocusedElement.focus({ preventScroll: true });
    } else if (opener && document.contains(opener)) {
      opener.focus({ preventScroll: true });
    }

    opener = null;
    lastFocusedElement = null;
    resetTransform();
  }

  function showSiblingImage(direction) {
    if (imageSequence.length < 2) return;

    const nextIndex = (currentIndex + direction + imageSequence.length) % imageSequence.length;
    showImageAt(nextIndex);
  }

  function showImageAt(index) {
    const imageData = imageSequence[index];
    if (!imageData?.src) return;

    currentIndex = index;
    opener = imageData.opener || opener;
    state.fallbackSrc = imageData.fallbackSrc || '';
    state.triedFallback = false;

    pointers.clear();
    resetTransform();
    elements.image.alt = imageData.alt || '';
    elements.image.removeAttribute('src');
    elements.root.classList.add('is-loading');
    elements.root.classList.remove('has-error');
    elements.image.src = imageData.src;
    updateNavigationControls();
  }

  function updateNavigationControls() {
    if (!elements) return;

    const hasNavigation = imageSequence.length > 1;
    elements.root.classList.toggle('has-navigation', hasNavigation);
    elements.previousButton.disabled = !hasNavigation;
    elements.nextButton.disabled = !hasNavigation;
  }

  function handleImageLoad() {
    elements.root.classList.remove('is-loading', 'has-error');
    resetTransform();
  }

  function handleImageError() {
    if (state.fallbackSrc && !state.triedFallback && elements.image.src !== state.fallbackSrc) {
      state.triedFallback = true;
      elements.image.src = state.fallbackSrc;
      return;
    }

    elements.root.classList.remove('is-loading');
    elements.root.classList.add('has-error');
  }

  function handleLightboxClick(event) {
    if (event.target.closest('[data-product-true-zoom-close]')) {
      closeZoom();
      return;
    }

    if (event.target === elements.stage || event.target === elements.backdrop) {
      if (state.ignoreStageClick) {
        state.ignoreStageClick = false;
        return;
      }

      closeZoom();
    }
  }

  function handleWheel(event) {
    if (!active) return;

    event.preventDefault();
    const nextScale = event.deltaY < 0 ? state.scale * WHEEL_ZOOM_STEP : state.scale / WHEEL_ZOOM_STEP;
    zoomAtPoint(nextScale, event.clientX, event.clientY);
  }

  function handleDoubleClick(event) {
    if (!active) return;

    event.preventDefault();
    if (state.scale > MIN_SCALE + 0.01) {
      resetTransform();
      return;
    }

    zoomAtPoint(DOUBLE_CLICK_SCALE, event.clientX, event.clientY);
  }

  function handlePointerDown(event) {
    if (!active || event.target.closest('.product-true-zoom__toolbar, .product-true-zoom__close')) return;
    if (event.target !== elements.image) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    elements.stage.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, getPointerPosition(event));

    if (state.scale > MIN_SCALE + 0.01 || pointers.size > 1) {
      event.preventDefault();
    }

    if (pointers.size === 1) {
      state.startX = event.clientX;
      state.startY = event.clientY;
      state.startTranslateX = state.x;
      state.startTranslateY = state.y;
      state.dragStarted = false;
    }

    if (pointers.size === 2) {
      event.preventDefault();
      state.ignoreStageClick = true;
      state.pinch = getPinchState();
    }
  }

  function handlePointerMove(event) {
    if (!active || !pointers.has(event.pointerId)) return;

    pointers.set(event.pointerId, getPointerPosition(event));

    if (pointers.size === 2 && state.pinch) {
      event.preventDefault();
      state.ignoreStageClick = true;
      updatePinch();
      return;
    }

    if (pointers.size !== 1 || state.scale <= MIN_SCALE) return;

    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    if (!state.dragStarted && Math.hypot(deltaX, deltaY) < 4) return;

    event.preventDefault();
    state.dragStarted = true;
    state.ignoreStageClick = true;
    elements.root.classList.add('is-dragging');
    state.x = state.startTranslateX + deltaX;
    state.y = state.startTranslateY + deltaY;
    applyTransform();
  }

  function handlePointerUp(event) {
    if (!pointers.has(event.pointerId)) return;

    pointers.delete(event.pointerId);
    elements.root.classList.remove('is-dragging');

    if (pointers.size < 2) {
      state.pinch = null;
    }

    if (pointers.size === 1) {
      const remainingPointer = Array.from(pointers.values())[0];
      state.startX = remainingPointer.x;
      state.startY = remainingPointer.y;
      state.startTranslateX = state.x;
      state.startTranslateY = state.y;
      state.dragStarted = false;
    }
  }

  function updatePinch() {
    const pinch = state.pinch;
    const current = getPinchMetrics();
    if (!pinch || !current || pinch.distance <= 0) return;

    state.scale = clamp(pinch.scale * (current.distance / pinch.distance), MIN_SCALE, MAX_SCALE);
    state.x = pinch.x + current.centerX - pinch.centerX;
    state.y = pinch.y + current.centerY - pinch.centerY;
    applyTransform();
  }

  function getPinchState() {
    const metrics = getPinchMetrics();
    if (!metrics) return null;

    return {
      ...metrics,
      scale: state.scale,
      x: state.x,
      y: state.y,
    };
  }

  function getPinchMetrics() {
    const activePointers = Array.from(pointers.values()).slice(0, 2);
    if (activePointers.length < 2) return null;

    const first = activePointers[0];
    const second = activePointers[1];
    const deltaX = second.x - first.x;
    const deltaY = second.y - first.y;

    return {
      distance: Math.hypot(deltaX, deltaY),
      centerX: (first.x + second.x) / 2,
      centerY: (first.y + second.y) / 2,
    };
  }

  function getPointerPosition(event) {
    return {
      x: event.clientX,
      y: event.clientY,
    };
  }

  function handleKeydown(event) {
    if (!active) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeZoom();
      return;
    }

    if (event.key === 'Tab') {
      trapFocus(event);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSiblingImage(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSiblingImage(1);
      return;
    }

    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomFromCenter(state.scale * BUTTON_ZOOM_STEP);
      return;
    }

    if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      zoomFromCenter(state.scale / BUTTON_ZOOM_STEP);
      return;
    }

    if (event.key === '0') {
      event.preventDefault();
      resetTransform();
    }
  }

  function trapFocus(event) {
    const focusableElements = Array.from(
      elements.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.disabled && element.getClientRects().length > 0);

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function handleResize() {
    if (!active) return;
    applyTransform();
  }

  function zoomFromCenter(nextScale) {
    const rect = elements.stage.getBoundingClientRect();
    zoomAtPoint(nextScale, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  function zoomAtPoint(nextScale, pointX, pointY) {
    const clampedScale = clamp(nextScale, MIN_SCALE, MAX_SCALE);

    if (clampedScale <= MIN_SCALE + 0.01) {
      resetTransform();
      return;
    }

    const imageRect = elements.image.getBoundingClientRect();
    const imageCenterX = imageRect.left + imageRect.width / 2;
    const imageCenterY = imageRect.top + imageRect.height / 2;
    const scaleRatio = clampedScale / state.scale;

    state.x += (pointX - imageCenterX) * (1 - scaleRatio);
    state.y += (pointY - imageCenterY) * (1 - scaleRatio);
    state.scale = clampedScale;
    applyTransform();
  }

  function resetTransform() {
    state.scale = MIN_SCALE;
    state.x = 0;
    state.y = 0;
    state.dragStarted = false;
    state.ignoreStageClick = false;
    state.pinch = null;
    applyTransform();
  }

  function applyTransform() {
    if (!elements) return;

    clampPan();
    elements.image.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;
    elements.root.classList.toggle('is-zoomed', state.scale > MIN_SCALE + 0.01);
    elements.zoomOutButton.disabled = state.scale <= MIN_SCALE + 0.01;
    elements.zoomInButton.disabled = state.scale >= MAX_SCALE - 0.01;
  }

  function clampPan() {
    if (!elements || state.scale <= MIN_SCALE + 0.01) {
      state.x = 0;
      state.y = 0;
      return;
    }

    const stageRect = elements.stage.getBoundingClientRect();
    const maxX = Math.max(0, (elements.image.offsetWidth * state.scale - stageRect.width) / 2);
    const maxY = Math.max(0, (elements.image.offsetHeight * state.scale - stageRect.height) / 2);

    state.x = clamp(state.x, -maxX, maxX);
    state.y = clamp(state.y, -maxY, maxY);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
